import type { ContextType } from "react";
import { Component } from "react";
import {
  BaseHighlightObject,
  SpanHighlightObject,
} from "../../../models/HighlightObject";
import {
  WindowProxyStore,
  WindowProxyStoreValue,
} from "../../../stores/impl/WindowProxyStore";
import {
  HighlightProviderState,
  HighlightStateContext,
} from "../../highlightProvider/HighlightProvider";
import { SpanHighlightProviderState } from "../../highlightProvider/SpanHighlightProvider";
import {
  IFrameCommunication,
  IFrameCommunication_BOX_OBJECT_MODIFIED,
  IFrameCommunication_CHANGE_INDEX,
  IFrameCommunication_HIGHLIGHT_SPANS,
  IFrameCommunication_HIGHLIGHT_SPANS_DATA,
  IFrameCommunication_REFRESH_CHILD,
  IFrameCommunication_SHIFT_PAGE,
} from "./IFrameCommunication";

export { RemoteImageViewer };

type Props = {
  spanHighlightContext: SpanHighlightProviderState;
  windowProxyStoreKey: string;
  /**
   * Title of panel in opened window
   */
  title: string;
  pageDimensions?: { width: number; height: number };
  totalPages?: number;
  pageIndex: number;
  imageUrl?: string;
  close: VoidFunction;
  changePageIndex: (pageIndex: number) => void;
  shiftPageByOne: (incrememnt: boolean) => void;
  /* Call to change whethere spans visible */
  showSpans: boolean;
  showSpansFn?: VoidFunction;
};

type State = {
  lastSpanContext: number;
  pageIndex: number;
  pageDimensions: { width: number; height: number } | undefined;
  docUuid: string;
  lastEnabled: boolean;
};

class RemoteImageViewer extends Component<Props, State> {
  static VIEWER_HTML_FILENAME = "viewer.html";
  static TARGET_ORIGIN = "*";

  static contextType = HighlightStateContext;
  declare context: ContextType<typeof HighlightStateContext>;

  iframeInterval: NodeJS.Timeout | null = null;
  windowProxy?: WindowProxy | null;

  constructor(props: Props) {
    super(props);
    this.state = {
      lastSpanContext: -1,
      pageIndex: -1,
      pageDimensions: undefined,
      docUuid: "",
      lastEnabled: false,
    };
  }

  UNSAFE_componentWillReceiveProps(
    nextProps: Props,
    nextContext: HighlightProviderState,
  ) {
    this.sendDataToChild(nextProps, nextContext);
    if (
      nextProps.spanHighlightContext.docUuid !== this.state.docUuid &&
      nextProps.spanHighlightContext.pageIndex !== this.state.pageIndex &&
      nextProps.spanHighlightContext.boxes.length !==
        this.state.lastSpanContext &&
      nextProps.spanHighlightContext.enabled != this.state.lastEnabled
    ) {
      // send span data
      this.windowProxy?.postMessage(
        {
          __acceleratorEvent: "",
          data: {
            highlight:
              nextProps.showSpansFn == undefined
                ? undefined
                : nextProps.showSpans,
            spanData: this.prepareHighlightBoxesForSerialization(
              nextProps.spanHighlightContext.boxes,
            ) as SpanHighlightObject[],
          },
          eventName: "HIGHLIGHT_SPANS_DATA",
          sender: "parent",
        } as IFrameCommunication_HIGHLIGHT_SPANS_DATA,
        RemoteImageViewer.TARGET_ORIGIN,
      );
    }
  }

  sendDataToChild(props: Readonly<Props>, context: HighlightProviderState) {
    const event: IFrameCommunication_REFRESH_CHILD = {
      __acceleratorEvent: "",
      data: {
        title: props.title,
        boxes: this.prepareHighlightBoxesForSerialization(context!.boxes),
        imageUrl: props.imageUrl,
        page: props.pageIndex,
        pageDimensions: props.pageDimensions,
        totalPages: props.totalPages,
        highlightSpans:
          props.showSpansFn == undefined ? undefined : props.showSpans,
      },
      eventName: "REFRESH_CHILD",
      sender: "parent",
    };
    this.windowProxy?.postMessage(event, RemoteImageViewer.TARGET_ORIGIN);
  }

  getIframeUrl(): string {
    const href = window.location.href;
    const hash = window.location.hash;
    const hashLessHref = href.substr(0, href.length - hash.length);
    return `${hashLessHref.substr(0, hashLessHref.lastIndexOf("/"))}/${RemoteImageViewer.VIEWER_HTML_FILENAME}`;
  }

  addEventListeners() {
    // how to know we've got a loaded window
    this.windowProxy?.addEventListener("load", () => {
      // push url through iframe events
      this.sendDataToChild(this.props, this.context!);
    });
    this.windowProxy?.addEventListener("message", (event: MessageEvent) => {
      const iframeEvent: IFrameCommunication = event.data;
      if (iframeEvent.__acceleratorEvent !== "") {
        return;
      }
      if (iframeEvent.sender === "parent") {
        return;
      }
      if (this.isShiftPageEvent(iframeEvent)) {
        this.props.shiftPageByOne(iframeEvent.data.increment);
      } else if (this.isChangeIndexEvent(iframeEvent)) {
        this.props.changePageIndex(iframeEvent.data.page);
      } else if (this.isObjectModifiedEvent(iframeEvent)) {
        const box = this.context?.boxes.find(
          (box) => box.id === iframeEvent.data.id,
        );
        if (!box) {
          console.warn(
            `RemoteImageViewer received update event for ID: ${iframeEvent.data.id}, box did not exist with this ID`,
          );
          return;
        }
        box.objectMoved(iframeEvent.data.modification);
      } else if (this.isHighlightSpansEvent(iframeEvent)) {
        this.props.showSpansFn && this.props.showSpansFn();
      }
    });
  }

  prepareHighlightBoxesForSerialization(
    boxes: BaseHighlightObject[],
  ): BaseHighlightObject[] {
    // console.debug('Sending highlight events');
    //@ts-expect-error
    return boxes.map((b) => ({
      ...b,
      objectMoved: null,
    }));
  }

  isObjectModifiedEvent(
    event: IFrameCommunication,
  ): event is IFrameCommunication_BOX_OBJECT_MODIFIED {
    return event.eventName === "BOX_OBJECT_MODIFIED";
  }
  isShiftPageEvent(
    event: IFrameCommunication,
  ): event is IFrameCommunication_SHIFT_PAGE {
    return event.eventName === "SHIFT_PAGE";
  }
  isHighlightSpansEvent(
    event: IFrameCommunication,
  ): event is IFrameCommunication_HIGHLIGHT_SPANS {
    return event.eventName === "HIGHLIGHT_SPANS";
  }
  isChangeIndexEvent(
    event: IFrameCommunication,
  ): event is IFrameCommunication_CHANGE_INDEX {
    return event.eventName === "CHANGE_INDEX";
  }

  startIframeCloseLoop() {
    // add iframe loop to figure out if this page is closed already
    if (this.iframeInterval == null) {
      // tf is this a log for
      //console.debug('Starting iFrame interval checker');
      this.iframeInterval = setInterval(() => {
        if (this.windowProxy != null && !this.windowProxy.closed) {
          return;
        }
        if (this.iframeInterval != null) {
          clearInterval(this.iframeInterval);
        }
        this.iframeInterval = null;
        //console.debug('Interval found IFrame closed');
        this.props.close();
      }, 20);
    }
  }

  open(): WindowProxy | null {
    // check if windowProxy already exists
    let storedWindowProxy = WindowProxyStore.getWithoutLookup(
      this.props.windowProxyStoreKey,
    );
    if (
      storedWindowProxy &&
      storedWindowProxy.window &&
      storedWindowProxy.window.closed
    ) {
      storedWindowProxy = undefined;
    }
    if (!storedWindowProxy) {
      storedWindowProxy = new WindowProxyStoreValue(
        this.props.windowProxyStoreKey,
        window.open(
          this.getIframeUrl(),
          "documentViewer",
          "resizable,scrollbars,status",
        )!,
      );
    }

    this.windowProxy = WindowProxyStore.set(storedWindowProxy, true).window;
    return this.windowProxy;
  }

  componentDidMount() {
    if (this.open()) {
      this.addEventListeners();
      this.startIframeCloseLoop();
    }
  }

  render() {
    return <span></span>;
  }
}
